import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, Book, School, FileText, Flag, 
  TrendingUp, ArrowUpRight, ArrowDownRight, 
  BarChart2, PieChart, Calendar, User, Mail
} from 'lucide-react';
import AuthContext from '../../contexts/AuthContext';

const AdminDashboardPage = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState({
    users: { total: 0, verified: 0, admins: 0, moderators: 0, recent: 0 },
    schools: { total: 0 },
    academic: { departments: 0, courses: 0, sections: 0 },
    materials: { total: 0, notes: 0, exams: 0, syllabi: 0, other: 0, reported: 0, recent: 0 },
    ratings: { total: 0, average: 0, recent: 0 },
    professors: { total: 0 }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAdminStats = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/admin/stats`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch admin statistics');
        }

        const data = await response.json();
        setStats(data);
      } catch (error) {
        console.error('Error fetching admin statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.role === 'admin') {
      fetchAdminStats();
    }
  }, [user?.role]);

  const StatCard = ({ title, value, icon, change, link }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
          <h3 className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">{loading ? '...' : value}</h3>
          
          {change && (
            <div className="mt-1 flex items-center">
              {change > 0 ? (
                <span className="text-green-500 flex items-center text-sm">
                  <ArrowUpRight size={16} className="mr-1" />
                  {change}% increase
                </span>
              ) : (
                <span className="text-red-500 flex items-center text-sm">
                  <ArrowDownRight size={16} className="mr-1" />
                  {Math.abs(change)}% decrease
                </span>
              )}
              <span className="text-gray-500 dark:text-gray-400 text-xs ml-1">last 30 days</span>
            </div>
          )}
        </div>
        
        <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
          {icon}
        </div>
      </div>
      
      {link && (
        <div className="mt-4">
          <Link to={link} className="text-red-600 hover:text-red-700 text-sm font-medium">
            View details →
          </Link>
        </div>
      )}
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Overview of EDX Red platform statistics
        </p>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Platform Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard 
            title="Total Users" 
            value={stats.users.total} 
            icon={<Users size={24} />}
            change={stats.users.recent > 0 ? Math.round((stats.users.recent / stats.users.total) * 100) : 0}
            link="/admin/users"
          />
          <StatCard 
            title="Schools" 
            value={stats.schools.total} 
            icon={<School size={24} />}
            link="/admin/schools"
          />
          <StatCard 
            title="Total Courses" 
            value={stats.academic.courses} 
            icon={<Book size={24} />}
            link="/admin/courses"
          />
          <StatCard 
            title="Total Materials" 
            value={stats.materials.total} 
            icon={<FileText size={24} />}
            change={stats.materials.recent > 0 ? Math.round((stats.materials.recent / stats.materials.total) * 100) : 0}
            link="/admin/materials"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">User Statistics</h3>
                <Link to="/admin/users" className="text-red-600 hover:text-red-700 text-sm font-medium">
                  View all users →
                </Link>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Users</p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-white mt-1">{stats.users.total}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Verified Users</p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-white mt-1">{stats.users.verified}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Administrators</p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-white mt-1">{stats.users.admins}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Moderators</p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-white mt-1">{stats.users.moderators}</p>
                </div>
              </div>
              
              <div className="mt-4 flex justify-center">
                <div className="p-4 w-full">
                  <div className="flex items-center mb-2">
                    <BarChart2 size={20} className="text-gray-400 mr-2" />
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">User Composition</p>
                  </div>
                  
                  <div className="relative pt-1">
                    <div className="flex h-4 overflow-hidden text-xs bg-gray-200 dark:bg-gray-700 rounded-full">
                      <div 
                        className="flex flex-col justify-center text-center text-white bg-red-600" 
                        style={{ width: `${(stats.users.admins / stats.users.total) * 100}%` }}
                      ></div>
                      <div 
                        className="flex flex-col justify-center text-center text-white bg-blue-500" 
                        style={{ width: `${(stats.users.moderators / stats.users.total) * 100}%` }}
                      ></div>
                      <div 
                        className="flex flex-col justify-center text-center text-white bg-green-500" 
                        style={{ width: `${(stats.users.verified / stats.users.total - stats.users.admins / stats.users.total - stats.users.moderators / stats.users.total) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="flex text-xs mt-2 justify-between text-gray-600 dark:text-gray-400">
                    <span className="flex items-center">
                      <span className="w-2 h-2 inline-block mr-1 bg-red-600 rounded-full"></span>
                      Admins
                    </span>
                    <span className="flex items-center">
                      <span className="w-2 h-2 inline-block mr-1 bg-blue-500 rounded-full"></span>
                      Moderators
                    </span>
                    <span className="flex items-center">
                      <span className="w-2 h-2 inline-block mr-1 bg-green-500 rounded-full"></span>
                      Students
                    </span>
                    <span className="flex items-center">
                      <span className="w-2 h-2 inline-block mr-1 bg-gray-300 dark:bg-gray-600 rounded-full"></span>
                      Unverified
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Material Statistics</h3>
                <Link to="/admin/materials" className="text-red-600 hover:text-red-700 text-sm font-medium">
                  View all materials →
                </Link>
              </div>
              
              <div className="flex mb-4">
                <div className="flex-1 text-center p-4">
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">{stats.materials.total}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Total Materials</div>
                </div>
                <div className="flex-1 text-center p-4 border-l border-gray-200 dark:border-gray-700">
                  <div className="text-3xl font-bold text-red-600 dark:text-red-400">{stats.materials.reported}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Open Reports</div>
                </div>
              </div>
              
              <div className="mt-4 flex justify-center">
                <div className="p-4 w-full">
                  <div className="flex items-center mb-2">
                    <PieChart size={20} className="text-gray-400 mr-2" />
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">Material Types</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Notes</span>
                        <span className="font-semibold text-blue-600 dark:text-blue-400">{stats.materials.notes}</span>
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Exams</span>
                        <span className="font-semibold text-red-600 dark:text-red-400">{stats.materials.exams}</span>
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Syllabi</span>
                        <span className="font-semibold text-green-600 dark:text-green-400">{stats.materials.syllabi}</span>
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded p-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-gray-300">Other</span>
                        <span className="font-semibold text-purple-600 dark:text-purple-400">{stats.materials.other}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Quick Actions</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              <Link 
                to="/admin/users" 
                className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="mr-4 p-3 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                  <Users size={20} />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Manage Users</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Add, edit, or delete users</p>
                </div>
              </Link>
              
              <Link 
                to="/admin/schools" 
                className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="mr-4 p-3 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                  <School size={20} />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Manage Schools</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Add or edit school information</p>
                </div>
              </Link>
              
              <Link 
                to="/admin/professors" 
                className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="mr-4 p-3 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                  <User size={20} />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Manage Professors</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Add, edit, or delete professors</p>
                </div>
              </Link>
              
              <Link 
                to="/admin/reports" 
                className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="mr-4 p-3 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                  <Flag size={20} />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Review Reports</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {stats.materials.reported + (stats.reports?.pending || 0)} items need review
                  </p>
                </div>
              </Link>
              
              <Link 
                to="/admin/courses" 
                className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="mr-4 p-3 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400">
                  <Book size={20} />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Manage Courses</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Add, edit, or delete courses</p>
                </div>
              </Link>
              
              <Link 
                to="/admin/email-blast" 
                className="flex items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
              >
                <div className="mr-4 p-3 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                  <Mail size={20} />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Email Blast</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Send announcements to users</p>
                </div>
              </Link>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <Calendar size={16} className="mr-1" />
                Last 30 days
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">New Users</h4>
                  <div className="text-green-500 flex items-center text-sm">
                    <TrendingUp size={16} className="mr-1" />
                    <span>{stats.users.recent}</span>
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  +{stats.users.recent}
                </p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">New Materials</h4>
                  <div className="text-green-500 flex items-center text-sm">
                    <TrendingUp size={16} className="mr-1" />
                    <span>{stats.materials.recent}</span>
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  +{stats.materials.recent}
                </p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">New Professor Ratings</h4>
                  <div className="text-green-500 flex items-center text-sm">
                    <TrendingUp size={16} className="mr-1" />
                    <span>{stats.ratings.recent}</span>
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  +{stats.ratings.recent}
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboardPage;